/**
 * home/sections/FeaturesSection.tsx
 * Sección Features — grid 2×2 layout A+B (home-v2.css)
 * Combina: número grande (B) + icono + badge + lista con dots (A)
 *
 * @version 3.0.0 — 2026-04-02
 */
import type { FC } from 'react';
import { FEATURES } from '../data';

// ── Color tokens por feature id ──────────────────────────────────────────────
const COLOR_MAP: Record<string, string> = {
  coords:     'lime',
  utf8:       'blue',
  validation: 'amber',
  geocoding:  'teal',
  export:     'teal',
};

// ── Número o label de impacto por feature id ─────────────────────────────────
const NUM_MAP: Record<string, string> = {
  coords:     '62',
  utf8:       '116',
  validation: '0–100',
  geocoding:  '18',
  export:     '10',
};

// ── Máximo 4 ítems visibles en el grid compacto ──────────────────────────────
const MAX_ITEMS = 4;

// ── Footer tags ───────────────────────────────────────────────────────────────
const FOOTER_TAGS = ['EPSG:25830', 'UTM30 ETRS89', '785 municipios · Andalucía'];

// ── COMPONENT ────────────────────────────────────────────────────────────────
export const FeaturesSection: FC = () => {
  // Mostrar solo las 4 primeras features en el grid 2×2
  const gridFeatures = FEATURES.slice(0, 4);

  return (
    <section className="features" id="features">
      <div className="container">

        {/* Cabecera de sección */}
        <div className="features__header">
          <h2 className="features__title">
            Todo lo que necesitas para normalizar datos&nbsp;PTEL
          </h2>
          <p className="features__subtitle">
            Cada módulo está diseñado para los problemas reales de los documentos municipales
          </p>
        </div>

        {/* Grid 2×2 */}
        <div className="features__grid">
          {gridFeatures.map((feature) => {
            const FeatureIcon = feature.icon;
            const color = COLOR_MAP[feature.id] ?? 'teal';
            const num   = NUM_MAP[feature.id]   ?? '';
            const items = feature.listTags.slice(0, MAX_ITEMS);

            return (
              <div
                key={feature.id}
                className={`feat-card-ab feat-card-ab--${color}`}
              >
                {/* Cabecera: icono + título + número */}
                <div className="feat-card-ab__header">
                  <div className="feat-card-ab__left">
                    <div className="feat-card-ab__icon">
                      <FeatureIcon />
                    </div>
                    <div>
                      <div className="feat-card-ab__title">{feature.title}</div>
                      <span className="feat-card-ab__badge">{feature.badge}</span>
                    </div>
                  </div>
                  <div className="feat-card-ab__num" aria-hidden="true">{num}</div>
                </div>

                {/* Divisor */}
                <div className="feat-card-ab__divider" />

                {/* Lista de capacidades */}
                <ul className="feat-card-ab__list">
                  {items.map((tag) => (
                    <li key={tag}>
                      <span className="feat-card-ab__dot" />
                      {tag}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer con tags técnicos */}
        <div className="features__footer">
          {FOOTER_TAGS.map((tag) => (
            <span key={tag} className="features__tag">{tag}</span>
          ))}
        </div>

      </div>
    </section>
  );
};

export default FeaturesSection;
